import { useCallback, useRef, useState } from 'react';
import {
  Auth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, getFirestoreDb } from '../firebase/firebase';
import { CreateUserProfileData } from '@shared/types';

// Shared, module-scoped flag used to temporarily suppress auth state updates during signup
export const isSigningUpRef = { current: false } as { current: boolean };

export interface UseSignupFlowResult {
  signUp: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  isSigningUp: boolean;
}

export function useSignupFlow(auth: Auth, firestoreDb: typeof db | null): UseSignupFlowResult {
  const [isSigningUp, setIsSigningUp] = useState(false);
  const localInFlight = useRef(false);

  const signUp = useCallback(
    async (email: string, password: string, name: string, phone?: string) => {
      // Begin suppression immediately to prevent any flicker from onAuthStateChanged
      isSigningUpRef.current = true;
      setIsSigningUp(true);
      localInFlight.current = true;

      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Best-effort: set display name for future sessions
        try {
          await updateProfile(user, { displayName: name });
        } catch {}

        // Best-effort: create a user profile document (keeps parity with existing flow)
        try {
          if (firestoreDb) {
            const freshDb = getFirestoreDb();
            const userRef = doc(freshDb, 'users', user.uid);
            const userProfileData: CreateUserProfileData = {
              name,
              email,
              role: 'player',
              signup_source: 'web',
              ...(phone && { phone }),
            };
            await setDoc(userRef, {
              ...userProfileData,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp(),
            });
          }
        } catch {}

        // Send verification and immediately sign out
        await sendEmailVerification(user);

        try {
          await signOut(auth);
        } catch {
          // Ignore sign out error; suppression prevents UI flicker and user remains effectively unauthenticated
        }
      } finally {
        // End suppression regardless of outcome
        localInFlight.current = false;
        setIsSigningUp(false);
        isSigningUpRef.current = false;
      }
    },
    [auth, firestoreDb]
  );

  return { signUp, isSigningUp };
}


